import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { COMPANY } from "@/i18n/translations";
import { MessageCircle, X, ArrowRight } from "lucide-react";

const buildQuickReplies = (t) => [
  {
    id: "quote",
    label: t("nav.getQuote"),
    message: "Hello Orion FM, I'd like a quote for my facility. Please share details.",
  },
  {
    id: "inspection",
    label: t("common.bookConsultation"),
    message: "Hello Orion FM, I'd like to book a free site inspection.",
  },
  {
    id: "amc",
    label: t("amc.sectionLabel"),
    message: "Hello Orion FM, please share your AMC/SLA proposal and pricing.",
  },
  {
    id: "manpower",
    label: t("services.manpower.title"),
    message: "Hello Orion FM, I need to discuss manpower/staff supply requirements.",
  },
];

export const ChatBot = () => {
  const { t, dir } = useLang();
  const [open, setOpen] = useState(false);
  const sideClass = dir === "rtl" ? "left-5" : "right-5";
  const quickReplies = buildQuickReplies(t);

  const openWhatsApp = (message) => {
    window.open(
      `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
    setOpen(false);
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chatbot-open"
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open chat"
          className={`fixed bottom-[152px] ${sideClass} z-40 w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-xl flex items-center justify-center transition-colors`}
        >
          <MessageCircle className="w-5 h-5" />
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className={`fixed bottom-[152px] ${sideClass} z-40 w-[320px] sm:w-[360px] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden`}
        >
          <div className="bg-slate-950 text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div className="leading-tight">
                <div className="font-heading text-sm font-medium">{COMPANY.brand}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-blue-300">
                  Online · replies in 1 min
                </div>
              </div>
            </div>
            <button
              data-testid="chatbot-close"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/10"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-5 max-h-[360px] overflow-y-auto">
            <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed">
              Hi 👋 I'm the Orion FM assistant. What can I help you with today?
            </div>
            <div className="mt-4 space-y-2">
              {quickReplies.map((q) => (
                <button
                  key={q.id}
                  data-testid={`chatbot-qr-${q.id}`}
                  onClick={() => openWhatsApp(q.message)}
                  className="w-full text-start flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white hover:border-blue-600 hover:bg-blue-50 px-4 py-3 text-sm font-medium text-slate-800 transition-colors"
                >
                  <span>{q.label}</span>
                  <ArrowRight className="w-4 h-4 text-blue-600 rtl-flip" />
                </button>
              ))}
            </div>
          </div>

          <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 text-center">
            Powered by WhatsApp · Replies go to a real human.
          </div>
        </div>
      )}
    </>
  );
};
